import { Injectable } from '@angular/core';
import { isSafeInternalUrl } from '../../shared/utils';

const STORAGE_KEY = 'post_login_redirect';

/**
 * URLs there would be no point returning to after a login.
 *
 * Remembering any of these is what used to destroy the user's deep link:
 * `saveRedirectGuard` fired on EVERY `:lang` activation, including the
 * intermediate hops the login flow itself produces, so a stored
 * `/es/ACME/reports/...` was overwritten before the callback could read it.
 */
const NON_RESTORABLE: readonly RegExp[] = [
  /^\/?$/, // the app root
  /^\/auth\//, // the callback routes
  /^\/unauthorized(?:$|[/?#])/, // the error page
  /^\/[a-z]{2}\/?(?:[?#]|$)/i, // a bare language root, e.g. '/es'
  /^\/[a-z]{2}\/select-company(?:$|[/?#])/i, // where a bare '/es' lands anyway
];

/**
 * Single owner of "where was the user going before we sent them to the IdP".
 *
 * The library has its own equivalent (`AutoLoginService`), but it cannot be
 * used here: `checkAuth()` consumes and deletes that value during bootstrap,
 * before any routed component can act on it.
 */
@Injectable()
export class PostLoginRedirectService {
  /** Remembers `url` unless it is a URL there would be no point returning to. */
  remember(url: string): void {
    if (
      !isSafeInternalUrl(url) ||
      NON_RESTORABLE.some((pattern) => pattern.test(url))
    ) {
      return;
    }

    try {
      sessionStorage.setItem(STORAGE_KEY, url);
    } catch {
      // Storage disabled (private mode): losing the deep link is acceptable,
      // failing the navigation is not.
    }
  }

  /** Reads and clears the remembered URL. Returns null when there is none. */
  consume(): string | null {
    try {
      const url = sessionStorage.getItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);

      return isSafeInternalUrl(url) ? url : null;
    } catch {
      return null;
    }
  }
}
